// A searchable picker over one or more adapter search functions (Who fields,
// Except, approvers). Searches as the admin types (debounced), loads on first
// open, keeps already-picked values visible even when the current page of
// results doesn't contain them.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select } from "antd";

const DEBOUNCE_MS = 250;

/**
 * props:
 *   sources   [{ key, label, search(query) -> Promise<[{ value, label?, sub? }]> }]
 *             One source: values are plain. Several: values are "<key>:<value>"
 *             and options are grouped under each source's label.
 *   prefixed  force "<key>:<value>" values even with one source
 *   depsKey   string; options reload when it changes (e.g. the domain id)
 *   value     string[] (or string with single)
 *   onChange, onBlur, placeholder, disabled, single, status, id, ariaLabel
 *   tagRender optional antd tagRender for the picked chips
 */
export default function RemotePicker({ sources, prefixed, depsKey = "", value, onChange, onBlur, placeholder, disabled, single, status, id, ariaLabel, tagRender }) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(false);
  const [state, setState] = useState({ loading: false, error: "", results: [] });
  const seq = useRef(0);
  const multi = !!prefixed || sources.length > 1;
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;

  useEffect(() => {
    setState({ loading: false, error: "", results: [] });
    setActive(false);
  }, [depsKey]);

  useEffect(() => {
    if (!active || disabled) return undefined;
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    const timer = setTimeout(() => {
      Promise.all(
        sourcesRef.current.map((src) =>
          Promise.resolve(src.search(query))
            .then((opts) => ({ src, opts: Array.isArray(opts) ? opts : [] }))
            .catch((e) => ({ src, opts: [], error: e?.message || `Couldn't load ${src.label.toLowerCase()}.` })),
        ),
      ).then((results) => {
        if (mine !== seq.current) return;
        const error = results.find((r) => r.error)?.error || "";
        setState({ loading: false, error, results });
      });
    }, query ? DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [active, query, depsKey, disabled]);

  const values = useMemo(() => (single ? (value ? [value] : []) : value || []).map(String), [value, single]);

  const options = useMemo(() => {
    const toOpt = (src, o) => ({
      value: multi ? `${src.key}:${o.value}` : String(o.value),
      label: o.sub ? `${o.label || o.value} · ${o.sub}` : o.label || String(o.value),
    });
    const seen = new Set();
    let out;
    if (multi) {
      out = state.results
        .filter((r) => r.opts.length)
        .map((r) => ({ label: r.src.label, title: r.src.label, options: r.opts.map((o) => toOpt(r.src, o)) }));
      out.forEach((g) => g.options.forEach((o) => seen.add(o.value)));
    } else {
      out = (state.results[0]?.opts || []).map((o) => toOpt(sources[0], o));
      out.forEach((o) => seen.add(o.value));
    }
    // Picked values not on this page of results still need a label.
    const missing = values.filter((v) => !seen.has(v)).map((v) => {
      const i = v.indexOf(":");
      const label = multi && i > 0 ? `${v.slice(i + 1)} (${(sources.find((s) => s.key === v.slice(0, i))?.label || "").toLowerCase().replace(/s$/, "")})` : v;
      return { value: v, label };
    });
    return multi ? [...(missing.length ? [{ label: "Picked", title: "Picked", options: missing }] : []), ...out] : [...missing, ...out];
  }, [state.results, values, multi, sources]);

  const notFound = state.loading ? "Loading…" : state.error || (query ? "No matches" : "Nothing to pick yet");

  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      mode={single ? undefined : "multiple"}
      allowClear={single}
      showSearch
      filterOption={false}
      value={single ? value || undefined : values}
      onChange={(v) => onChange(single ? v || "" : v)}
      onSearch={setQuery}
      onFocus={() => setActive(true)}
      onOpenChange={(open) => open && setActive(true)}
      onBlur={() => {
        setQuery("");
        if (onBlur) onBlur();
      }}
      placeholder={placeholder}
      disabled={disabled}
      loading={state.loading}
      options={options}
      notFoundContent={notFound}
      status={status}
      maxTagCount={tagRender ? undefined : "responsive"}
      tagRender={tagRender}
      style={{ width: "100%" }}
    />
  );
}
